import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from "react";
import * as Astro from "./astronomy.js";
import {
  LayoutDashboard,
  Zap,
  Target,
  Activity,
  Globe,
  Sun,
  Menu,
  X,
  Calendar,
  Settings,
  ChevronRight,
  ChevronLeft,
  Clock,
  Search,
  RefreshCw,
  MoveVertical,
  Gauge,
  Moon,
  ArrowLeft,
  MapPin,
  LayoutGrid,
  Layers,
  CircleDot,
  Hash,
  Plus,
  Upload,
  FileText,
  TrendingUp,
  MoreVertical,
  Crosshair,
  Copy,
} from "lucide-react";

// コンポーネントインポート
import AssetSelector from "./components/Common/AssetSelector.jsx";
import CriticalDegreesSection from "./components/Financial/CriticalDegreesSection.jsx";
import AspectSection from "./components/Financial/AspectSection.jsx";
import Dial90Chart from "./components/Financial/Dial90Chart.jsx";
import HoroscopeChart from "./components/Financial/HoroscopeChart.jsx";
import RetrogradeSpeedSection from "./components/Financial/RetrogradeSpeedSection.jsx";
import MoonStateSection from "./components/Financial/MoonStateSection.jsx";
import CelestialEventsSection from "./components/Financial/CelestialEventsSection.jsx";
import AspectSearchSection from "./components/Financial/AspectSearchSection.jsx";
import PlanetReturnSection from "./components/Financial/PlanetReturnSection.jsx";
import DeclinationAspectsSection from "./components/Financial/DeclinationAspectsSection.jsx";
import PlanetInfoTransit from "./components/Financial/PlanetInfoTransit.jsx";
import DailyEventsBlock from "./components/Financial/DailyEventsBlock.jsx"; // 追加
import AstrologyReportSection from "./components/Financial/AstrologyReportSection.jsx"; // 追加
import { parseInvestingCSV } from "./utils/csvParser.js";
import ChartAnalysisGate from "./components/Financial/ChartAnalysisGate.jsx";
import SolarArcMidpointDial from "./components/Financial/SolarArcMidpointDial.jsx";
import DashboardGate from "./components/Financial/DashboardGate.jsx";
import EphemerisChartSection from "./components/Financial/EphemerisChartSection.jsx";
import { styles as commonStyles } from "./utils/styles.js";
import logoImage from "./assets/logo.png";
import Header from "./components/Financial/Header.jsx";
import {
  DEFAULT_PLANET_COLORS,
  DEFAULT_SIGN_COLORS,
  PLANET_COLORS as STATIC_COLORS, // 名前が被らないようにリネーム
} from "./utils/constants.js";
import SettingsSection from "./components/Financial/SettingsSection.jsx";
import CryptoChartBlock from "./components/Financial/CryptoChartBlock.jsx";
// utils/constants.js
import {
  PLANET_SYMBOLS,
  PLANETS,
  PLANET_SCORES,
  ASP_SCORES,
  DEFAULT_ASSETS,
  PLANET_COLORS,
} from "./utils/constants.js";
const getExtendedPoints = Astro.getExtendedPoints;
import { MIDPOINT_AXES, TRIGGER_PLANETS } from "./utils/interpretations.js";

const UPLOADED_CHARTS_KEY = "astronica_uploaded_csv_data";

const isMajor = (name) =>
  ["Conjunction", "Sextile", "Square", "Trine", "Opposition"].includes(name);
const isSoft = (name) => ["Sextile", "Trine"].includes(name);
const isHard = (name) => ["Conjunction", "Square", "Opposition"].includes(name);

// --- ヘルパー関数の統合版 (エラー防止と機能維持) ---
const getSquareLabel = (p1, lon1, p2, lon2) => {
  // パターンA: 引数が1つ（数値）だけ渡された場合、以前の単純なラベルを返す
  if (lon1 === undefined) {
    if (p1 === null || p1 === undefined) return "N/A";
    return p1 + "° Contact";
  }

  // パターンB: 引数が4つ渡された場合、詳細な WAX/WAN 判定を行う
  const order = [
    "Moon",
    "Mercury",
    "Venus",
    "Sun",
    "Mars",
    "Jupiter",
    "Saturn",
    "Uranus",
    "Neptune",
    "Pluto",
  ];
  const idx1 = order.indexOf(p1);
  const idx2 = order.indexOf(p2);

  let fastLon = lon1,
    slowLon = lon2;
  if (idx1 !== -1 && idx2 !== -1 && idx1 > idx2) {
    fastLon = lon2;
    slowLon = lon1;
  }

  const phase = (fastLon - slowLon + 360) % 360;
  return phase < 180 ? "(WAX)" : "(WAN)";
};

const TOGGLE_CONFIG = {
  heliocentric: {
    on: "ヘリオセントリック(太陽中心)",
    off: "ジオセントリック(地球中心)",
  },
  zodiac: { on: "サイデリアル(恒星黄道帯)", off: "トロピカル(回帰黄道帯)" },
  display: { on: "拡張表示(ASC/MC/ノード/小惑星)", off: "10惑星のみ" },
};

const CITIES = [
  { name: "東京", lat: 35.6895, lon: 139.6917 },
  { name: "大阪", lat: 34.6937, lon: 135.5023 },
  { name: "ニューヨーク", lat: 40.7128, lon: -74.006 },
  { name: "ロンドン", lat: 51.5074, lon: -0.1278 },
  { name: "シドニー", lat: -33.8688, lon: 151.2093 },
];

const STORAGE_KEY = "astronica_custom_assets";
const SELECTED_ASSET_KEY = "astronica_selected_asset";
// DSC, IC を追加して AspectSection と同期
const ASPECT_POINTS = [...PLANETS, "NorthNode", "ASC", "DSC", "MC", "IC"];

const SIGNAL_DATA = {
  Cardinal: {
    bearish: [
      "Jupiter/Pluto",
      "Uranus/Neptune",
      "Mars/Pluto",
      "Saturn/Neptune",
      "Venus/Pluto",
      "Sun/Saturn",
      "Sun/Venus",
      "Mercury/Saturn",
      "Moon/Neptune",
      "Mars/Neptune",
      "Saturn/Pluto",
      "Mercury/Pluto",
    ],
    bullish: [
      "Mars/Uranus",
      "Moon/Mars",
      "Moon/Mercury",
      "Moon/Pluto",
      "Sun/Mars",
      "Moon/Venus",
      "Venus/Jupiter",
      "Sun/Neptune",
      "Mercury/Jupiter",
      "Moon/Jupiter",
      "Venus/Neptune",
      "Mercury/Mars",
      "Mars/Saturn",
      "Venus/Mars",
      "Venus/Uranus",
      "Jupiter/Saturn",
      "Moon/Saturn",
      "Mars/Jupiter",
      "Saturn/Uranus",
      "Neptune/Pluto",
    ],
  },
  Fixed: {
    bearish: [
      "Jupiter/Neptune",
      "Saturn/Uranus",
      "Venus/Jupiter",
      "Mars/Saturn",
      "Venus/Neptune",
      "Mars/Neptune",
      "Saturn/Neptune",
      "Moon/Uranus",
      "Mercury/Venus",
      "Mercury/Jupiter",
      "Sun/Pluto",
      "Moon/Jupiter",
      "Moon/Venus",
      "Mars/Jupiter",
      "Mars/Uranus",
      "Neptune/Pluto",
      "Sun/Mercury",
      "Sun/Jupiter",
      "Mercury/Neptune",
      "Moon/Saturn",
      "Jupiter/Uranus",
      "Mercury/Mars",
    ],
    bullish: [
      "Sun/Saturn",
      "Sun/Venus",
      "Saturn/Pluto",
      "Moon/Neptune",
      "Mercury/Uranus",
      "Mercury/Saturn",
      "Sun/Uranus",
      "Jupiter/Pluto",
      "Venus/Mars",
      "Venus/Saturn",
      "Jupiter/Saturn",
      "Mars/Pluto",
      "Uranus/Pluto",
    ],
  },
  Mutable: {
    bearish: [
      "Jupiter/Saturn",
      "Saturn/Uranus",
      "Venus/Mars",
      "Uranus/Pluto",
      "Venus/Saturn",
      "Venus/Uranus",
      "Mars/Jupiter",
      "Moon/Saturn",
      "Sun/Mars",
      "Mars/Pluto",
      "Mercury/Mars",
      "Moon/Pluto",
      "Moon/Mercury",
      "Sun/Neptune",
      "Sun/Uranus",
      "Mercury/Uranus",
      "Moon/Mars",
    ],
    bullish: [
      "Mercury/Neptune",
      "Sun/Jupiter",
      "Mercury/Pluto",
      "Moon/Neptune",
      "Sun/Venus",
      "Sun/Mercury",
      "Sun/Saturn",
      "Jupiter/Pluto",
      "Venus/Neptune",
      "Mercury/Venus",
      "Moon/Uranus",
      "Venus/Jupiter",
      "Venus/Pluto",
      "Sun/Pluto",
      "Saturn/Neptune",
      "Mars/Neptune",
      "Jupiter/Neptune",
      "Uranus/Neptune",
    ],
  },
};

const TOP_BEARISH = [
  "Jupiter/Saturn",
  "Jupiter/Neptune",
  "Saturn/Uranus",
  "Venus/Mars",
  "Jupiter/Pluto",
  "Uranus/Neptune",
  "Uranus/Pluto",
];
const TOP_BULLISH = [
  "Neptune/Pluto",
  "Uranus/Pluto",
  "Saturn/Uranus",
  "Uranus/Neptune",
  "Mars/Pluto",
  "Jupiter/Neptune",
  "Mars/Neptune",
];

const ASPECT_DEFINITIONS = [
  {
    name: "Conjunction",
    angle: 0,
    symbol: "☌",
    type: "Hard",
    category: "major",
  },
  {
    name: "Opposition",
    angle: 180,
    symbol: "☍",
    type: "Hard",
    category: "major",
  },
  { name: "Trine", angle: 120, symbol: "△", type: "Soft", category: "major" },
  { name: "Square", angle: 90, symbol: "□", type: "Hard", category: "major" },
  { name: "Sextile", angle: 60, symbol: "⚹", type: "Soft", category: "major" },
  {
    name: "Semi-Sextile",
    angle: 30,
    symbol: "⚺",
    type: "Soft",
    category: "minor",
  },
  {
    name: "Quincunx",
    angle: 150,
    symbol: "ㅈ",
    type: "Hard",
    category: "minor",
  },
  { name: "Decile", angle: 36, symbol: "⊥", type: "Soft", category: "minor" },
  {
    name: "Bi-Decile",
    angle: 72,
    symbol: "D2",
    type: "Soft",
    category: "minor",
  },
  {
    name: "Tri-Decile",
    angle: 108,
    symbol: "D3",
    type: "Hard",
    category: "minor",
  },
  {
    name: "Quadra-Decile",
    angle: 144,
    symbol: "D4",
    type: "Soft",
    category: "minor",
  },
  {
    name: "Semi-Decile",
    angle: 18,
    symbol: "SD",
    type: "Soft",
    category: "minor",
  },
  { name: "Novile", angle: 40, symbol: "N", type: "Soft", category: "minor" },
  {
    name: "Bi-Novile",
    angle: 80,
    symbol: "N2",
    type: "Soft",
    category: "minor",
  },
  {
    name: "Quad-Novile",
    angle: 160,
    symbol: "N4",
    type: "Hard",
    category: "minor",
  },
  {
    name: "Semi-Novile",
    angle: 20,
    symbol: "SN",
    type: "Soft",
    category: "minor",
  },
  {
    name: "Semi-Square",
    angle: 45,
    symbol: "∠",
    type: "Hard",
    category: "minor",
  },
  {
    name: "Sesquiquadrate",
    angle: 135,
    symbol: "⚼",
    type: "Hard",
    category: "minor",
  },
  {
    name: "Septile",
    angle: 51.43,
    symbol: "S",
    type: "Hard",
    category: "minor",
  },
  {
    name: "Bi-Septile",
    angle: 102.86,
    symbol: "S2",
    type: "Hard",
    category: "minor",
  },
  {
    name: "Tri-Septile",
    angle: 154.29,
    symbol: "S3",
    type: "Hard",
    category: "minor",
  },
  { name: "Quintile", angle: 72, symbol: "Q", type: "Soft", category: "minor" },
  {
    name: "Bi-Quintile",
    angle: 144,
    symbol: "bQ",
    type: "Soft",
    category: "minor",
  },
  {
    name: "Undecile",
    angle: 165,
    symbol: "U",
    type: "Hard",
    category: "minor",
  },
];

export default function App() {
  // --- 1. ステート定義を最上部に集約 (ReferenceError を確実に防止) ---
  const [isDarkMode, setIsDarkMode] = useState(function () {
    const saved = localStorage.getItem("astronica_theme");
    return saved ? saved === "dark" : true;
  });

  // --- 修正版：デフォルト東京、ハウスシステム設定の追加 ---
  const [observerLat, setObserverLat] = useState(function () {
    var saved = localStorage.getItem("astronica_observer_lat");
    return saved !== null && saved !== "undefined" ? Number(saved) : 35.6895; // 東京
  });
  const [observerLon, setObserverLon] = useState(function () {
    var saved = localStorage.getItem("astronica_observer_lon");
    return saved !== null && saved !== "undefined" ? Number(saved) : 139.6917; // 東京
  });
  const [transitTz, setTransitTz] = useState(function () {
    var saved = localStorage.getItem("astronica_transit_tz");
    return saved !== null && saved !== "undefined" ? Number(saved) : 9; // デフォルト：東京(+9)
  });
  const [houseSystem, setHouseSystem] = useState(function () {
    return localStorage.getItem("astronica_house_system") || "placidus";
  });

  // --- 1. カラー設定のステート管理を追加 ---
  const [planetColors, setPlanetColors] = useState(function () {
    const saved = localStorage.getItem("astronica_planet_colors");
    return saved ? JSON.parse(saved) : DEFAULT_PLANET_COLORS;
  });

  const [signColors, setSignColors] = useState(function () {
    const saved = localStorage.getItem("astronica_sign_colors");
    return saved ? JSON.parse(saved) : DEFAULT_SIGN_COLORS;
  });

  // 保存用Effect
  useEffect(
    function () {
      localStorage.setItem(
        "astronica_planet_colors",
        JSON.stringify(planetColors),
      );
      localStorage.setItem("astronica_sign_colors", JSON.stringify(signColors));
    },
    [planetColors, signColors],
  );

  const handleResetColors = function () {
    if (window.confirm("すべての色設定をデフォルトに戻しますか？")) {
      setPlanetColors(DEFAULT_PLANET_COLORS);
      setSignColors(DEFAULT_SIGN_COLORS);
    }
  };

  // ステートの初期値を修正

  // --- [NEW] 試用期間・ライセンス認証管理 ---
  const isLocal = useMemo(() => {
    const hostname = window.location.hostname;
    return (
      hostname === "localhost" ||
      hostname === "127.0.0.1" ||
      hostname.startsWith("192.168.") ||
      hostname.startsWith("10.") ||
      hostname.endsWith(".local")
    );
  }, []);

  const [isDashboardUnlocked, setIsDashboardUnlocked] = useState(() => {
    return localStorage.getItem("astronica_dashboard_unlocked") === "true";
  });

  const [trialStartedAt, setTrialStartedAt] = useState(() => {
    const saved = localStorage.getItem("astronica_trial_started_at");
    return saved ? parseInt(saved, 10) : null;
  });

  const [isTrialActive, setIsTrialActive] = useState(() => {
    const saved = localStorage.getItem("astronica_trial_started_at");
    if (!saved) return false;
    const startTime = parseInt(saved, 10);
    const now = Date.now();
    const twentyFourHours = 24 * 60 * 60 * 1000;
    return now - startTime < twentyFourHours;
  });

  useEffect(() => {
    // 試用期間の開始判定（まだ開始されていない場合のみ）
    if (!trialStartedAt) {
      // URLパラメータのチェック（トークンがある場合、または初回アクセス）
      const params = new URLSearchParams(window.location.search);
      const hasToken = params.has("token");

      // トークンがある場合、または特定の条件下で試用を開始（既存仕様を維持しつつ整理）
      // ※ここでは「初回アクセスで自動開始」の挙動を維持
      const startTime = Date.now();
      localStorage.setItem("astronica_trial_started_at", startTime.toString());
      setTrialStartedAt(startTime);
      setIsTrialActive(true);
    } else {
      // すでに開始されている場合は、時間経過を再判定（インターバル等で更新が必要な場合のため）
      const now = Date.now();
      const twentyFourHours = 24 * 60 * 60 * 1000;
      if (now - trialStartedAt < twentyFourHours) {
        setIsTrialActive(true);
      } else {
        setIsTrialActive(false);
      }
    }
  }, [trialStartedAt]);

  const handleUnlockDashboard = () => {
    setIsDashboardUnlocked(true);
    localStorage.setItem("astronica_dashboard_unlocked", "true");
  };

  const [activeSection, setActiveSection] = useState("dashboard");

  // サイドバー管理 (デフォルト: collapsed=true)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);

  // CSVパース中のローディング状態を管理
  const [isParsing, setIsParsing] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(function () {
    const handleScroll = function () {
      // 20px以上スクロールしたかどうかを判定
      if (window.scrollY > 20) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return function () {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  /* --- App.jsx：ステート定義セクション (230行目付近) --- */

  // アスペクト別・天体速度別のOrb設定ステート
  const [aspectSettings, setAspectSettings] = useState(function () {
    const saved = localStorage.getItem("astronica_aspect_settings");
    if (saved !== null) {
      return JSON.parse(saved);
    }
    return {
      majorOrb: 8.0, // メジャーアスペクト標準
      minorOrb: 2.0, // マイナーアスペクト標準
      outerPlanetOrb: 1.0, // 外惑星同士の接触制限
      useOuterLimit: false, // 外惑星制限を有効にするか
    };
  });

  // 【追加】ホロスコープ表示スタイルのステート
  const [horoscopeStyle, setHoroscopeStyle] = useState(function () {
    const saved = localStorage.getItem("astronica_horoscope_style");
    return saved || "style1";
  });

  // 【追加】アスペクトライン非表示設定のステート
  const [hideAspectLines, setHideAspectLines] = useState(function () {
    return localStorage.getItem("astronica_hide_aspect_lines") === "true";
  });

  // 保存用
  useEffect(
    function () {
      localStorage.setItem("astronica_theme", isDarkMode ? "dark" : "light");
    },
    [isDarkMode],
  );

  // 設定保存用に関数を追加
  const handleSaveAspectSettings = function (newSettings) {
    setAspectSettings(newSettings);
    localStorage.setItem(
      "astronica_aspect_settings",
      JSON.stringify(newSettings),
    );
  };

  // 【追加】表示スタイル変更ハンドラ
  const handleHoroscopeStyleChange = function (style) {
    setHoroscopeStyle(style);
    localStorage.setItem("astronica_horoscope_style", style);
  };

  // 【追加】ライン非表示設定変更ハンドラ
  const handleHideAspectLinesChange = function (hide) {
    setHideAspectLines(hide);
    localStorage.setItem(
      "astronica_hide_aspect_lines",
      hide ? "true" : "false",
    );
  };

  // --- チャート管理ステート（複数保持対応） ---

  const [uploadedCharts, setUploadedCharts] = useState(() => {
    try {
      const saved = localStorage.getItem(UPLOADED_CHARTS_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      return [];
    }
  });

  const [activeChartId, setActiveChartId] = useState(() => {
    const saved = localStorage.getItem(UPLOADED_CHARTS_KEY);
    if (saved) {
      const charts = JSON.parse(saved);
      return charts.length > 0 ? charts[0].id : null;
    }
    return null;
  });

  // データが変更されるたびに保存するEffect
  useEffect(() => {
    localStorage.setItem(UPLOADED_CHARTS_KEY, JSON.stringify(uploadedCharts));
  }, [uploadedCharts]);

  // 現在選択されているチャートデータを取得（メモ化）
  const currentUploadedData = useMemo(() => {
    const chart = uploadedCharts.find((c) => c.id === activeChartId);
    return chart ? chart.data : [];
  }, [uploadedCharts, activeChartId]);

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200,
  );

  const [selectedAssetId, setSelectedAssetId] = useState(
    () => localStorage.getItem(SELECTED_ASSET_KEY) || "btc",
  );
  const [transitDate, setTransitDate] = useState(
    new Date().toISOString().split("T")[0],
  );

  const [transitTime, setTransitTime] = useState("12:00");

  const [customAssets, setCustomAssets] = useState(() =>
    JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]"),
  );
  const [importantDates, setImportantDates] = useState(() =>
    JSON.parse(localStorage.getItem("astronica_important_dates") || "{}"),
  );

  const [isHeliocentric, setIsHeliocentric] = useState(false);
  const [isSidereal, setIsSidereal] = useState(false);
  const [showNatalPositions, setShowNatalPositions] = useState(false);
  // 拡張表示（ASC/MC/Node）の切り替えステートを追加
  const [showExtendedPoints, setShowExtendedPoints] = useState(true);
  // 保存処理を1つに統合：設定の逆戻りを防止
  useEffect(
    function () {
      localStorage.setItem("astronica_observer_lat", String(observerLat));
      localStorage.setItem("astronica_observer_lon", String(observerLon));
      localStorage.setItem("astronica_transit_tz", String(transitTz));
      localStorage.setItem("astronica_house_system", houseSystem);
      console.log("--- [DEBUG: App] 設定を同期しました ---", {
        observerLat,
        observerLon,
        transitTz,
        houseSystem,
      });
    },
    [observerLat, observerLon, transitTz, houseSystem],
  );

  // 保存処理の統合（transitTz を確実に対象に含める）
  useEffect(
    function () {
      console.log("--- [DEBUG: App] 設定を保存しました ---", {
        observerLat,
        observerLon,
        houseSystem,
        transitTz,
      });
      localStorage.setItem("astronica_observer_lat", String(observerLat));
      localStorage.setItem("astronica_observer_lon", String(observerLon));
      localStorage.setItem("astronica_house_system", houseSystem);
      localStorage.setItem("astronica_transit_tz", String(transitTz)); // 追加
    },
    [observerLat, observerLon, houseSystem, transitTz],
  );

  const [isCustomLocation, setIsCustomLocation] = useState(false);
  const [orbFilter, setOrbFilter] = useState(1.5);

  // --- ミッドポイント・選択初期状態の追加 (ReferenceError 対策) ---
  const [selectedTriggers, setSelectedTriggers] = useState([]);
  const [selectedNatalBodies, setSelectedNatalBodies] = useState([]);

  // userLocation のメモ化定義を追加
  const userLocation = useMemo(
    () => ({ latitude: observerLat, longitude: observerLon }),
    [observerLat, observerLon]
  );

  const [chartMode, setChartMode] = useState("transit");
  const [aspectCategory, setAspectCategory] = useState("all");

  useEffect(function () {
    const handleResize = function () {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener("resize", handleResize);
    return function () {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const isMobile = windowWidth <= 1024;
  const allAssets = useMemo(
    () => [...DEFAULT_ASSETS, ...customAssets],
    [customAssets],
  );
  const selectedAsset = useMemo(
    () => allAssets.find((a) => a.id === selectedAssetId) || DEFAULT_ASSETS[0],
    [allAssets, selectedAssetId],
  );

  /**
   * タイムゾーンを考慮した絶対時間(UTC)の生成
   */
  const transitDateObj = useMemo(
    function () {
      if (!transitDate || !transitTime) return new Date();
      var parts = transitDate.split("-");
      var timeParts = transitTime.split(":");
      var year = parseInt(parts[0]);
      var month = parseInt(parts[1]) - 1;
      var day = parseInt(parts[2]);
      var hour = parseInt(timeParts[0]);
      var min = parseInt(timeParts[1]);

      // UTCミリ秒を算出し、タイムゾーン分を「引き算」して絶対UTCを求める
      // 例: 東京 12:00(+9) -> 12 - 9 = 03:00 UTC
      var utcMs = Date.UTC(year, month, day, hour, min) - transitTz * 3600000;
      var date = new Date(utcMs);

      console.log("--- [DEBUG: App] 計算用時刻(UTC):", date.toISOString());
      return date;
    },
    [transitDate, transitTime, transitTz],
  );

  const natalDateObj = useMemo(
    () =>
      Astro.createDate(
        selectedAsset.date,
        selectedAsset.time,
        selectedAsset.timezone,
      ),
    [selectedAsset],
  );

  // セクション（メニュー）切り替え時に表示位置を最上部にリセットする
  useEffect(
    function () {
      window.scrollTo(0, 0);
    },
    [activeSection],
  );

  // --- 2. 計算エンジンの依存配列に houseSystem を追加 ---
  const getAdjustedPositions = useCallback(
    (date, isHelio) => {
      let pos = isHelio
        ? Astro.getAllHeliocentricLongitudes(date)
        : Astro.getAllPlanetLongitudesExtended(date, observerLat, observerLon);

      if (!isHelio) {
        // 第4引数に houseSystem を渡す
        const extendedPoints = Astro.getExtendedPoints(
          date,
          observerLat,
          observerLon,
          houseSystem,
        );
        pos = Object.assign({}, pos, extendedPoints);
      }

      // デバッグ：計算されたASCを確認
      console.log("--- [DEBUG: App] 計算結果のASC:", pos.ASC);

      if (isSidereal && date) {
        const currentAyanamsha = Astro.calculateLahiriAyanamsha(date);
        const siderealPos = {};
        for (const [p, lon] of Object.entries(pos)) {
          if (lon === null || lon === undefined) {
            siderealPos[p] = null;
            continue;
          }
          siderealPos[p] = (lon - currentAyanamsha + 360) % 360;
        }
        return siderealPos;
      }
      return pos;
    },
    [isSidereal, observerLat, observerLon, isHeliocentric, houseSystem],
  );

  // --- 天体位置計算：ガード追加版 ---
  const transitPositions = useMemo(
    function () {
      // 日付が無効な場合は空オブジェクトを返して計算を即終了
      if (transitDateObj === null || transitDateObj === undefined) {
        return {};
      }
      return getAdjustedPositions(transitDateObj, isHeliocentric);
    },
    [transitDateObj, isHeliocentric, getAdjustedPositions],
  );
  const natalPositions = useMemo(
    () => getAdjustedPositions(natalDateObj, isHeliocentric),
    [natalDateObj, isHeliocentric, getAdjustedPositions],
  );

  // --- 天体速度計算：ガード追加版 ---
  const transitSpeeds = useMemo(
    function () {
      // 日付が無効な場合は空オブジェクトを返して計算を即終了
      if (transitDateObj === null || transitDateObj === undefined) {
        return {};
      }

      const posCurrent = getAdjustedPositions(transitDateObj, isHeliocentric);
      const nextDay = new Date(transitDateObj.getTime() + 24 * 60 * 60 * 1000);
      const posNext = getAdjustedPositions(nextDay, isHeliocentric);
      const speeds = {};

      Object.keys(posCurrent).forEach(function (id) {
        const cur = posCurrent[id];
        const nxt = posNext[id];
        if (
          cur !== undefined &&
          nxt !== undefined &&
          cur !== null &&
          nxt !== null
        ) {
          let diff = nxt - cur;
          if (diff > 180) diff -= 360;
          if (diff < -180) diff += 360;
          speeds[id] = diff;
        }
      });
      return speeds;
    },
    [transitDateObj, isHeliocentric, getAdjustedPositions],
  );

  const isRetro = useCallback(
    (planetId) => {
      if (!planetId) return false;
      if (planetId.includes("Node")) return true;
      if (isHeliocentric || planetId === "Sun" || planetId === "Moon")
        return false;
      if (!transitSpeeds) return false;
      const speed =
        transitSpeeds[planetId] ??
        transitSpeeds[
        planetId.charAt(0).toUpperCase() + planetId.slice(1).toLowerCase()
        ];
      return typeof speed === "number" && speed < 0;
    },
    [transitSpeeds, isHeliocentric],
  );

  // --- ミッドポイント解析：ガード追加版 ---
  const analysisResults = useMemo(
    function () {
      // データが揃っていない場合は解析を行わない
      if (transitPositions === null || natalPositions === null) {
        return null;
      }

      const midpoints = [];
      // 重複排除のために Set を使用
      const planetsForMid = Array.from(new Set([...PLANETS, "NorthNode", "ASC", "MC", "Aries"]));
      const triggerPlanets = Array.from(new Set([...PLANETS, "NorthNode", "ASC", "MC", "Aries"]));

      for (let i = 0; i < planetsForMid.length; i++) {
        for (let j = i + 1; j < planetsForMid.length; j++) {
          const p1 = planetsForMid[i];
          const p2 = planetsForMid[j];

          // 自己ペア（同じ天体同士）を確実に除外
          if (p1 === p2) continue;

          // Aries Point (♈) の場合は 0度固定として扱う
          const pos1 = p1 === "Aries" ? 0 : natalPositions[p1];
          const pos2 = p2 === "Aries" ? 0 : natalPositions[p2];

          if (pos1 === undefined || pos2 === undefined) continue;

          const mid = Astro.calculateMidpoint(pos1, pos2);
          if (mid === null) continue;

          const mod90 = Astro.getMod90(mid);
          const modality = Astro.getModality(mod90);

          triggerPlanets.forEach(function (tp) {
            const tPos = tp === "Aries" ? 0 : transitPositions[tp];
            if (tPos === undefined) return;

            const tMod90 = Astro.getMod90(tPos);
            const orb = Astro.calculateOrb(tMod90, mod90);
            if (orb <= orbFilter) {
              const pair = p1 + "/" + p2;
              const revPair = p2 + "/" + p1;
              let signal = "neutral";
              const sd = SIGNAL_DATA[modality];

              if (
                sd &&
                sd.bearish &&
                (sd.bearish.indexOf(pair) !== -1 ||
                  sd.bearish.indexOf(revPair) !== -1)
              ) {
                signal = "bearish";
              } else if (
                sd &&
                sd.bullish &&
                (sd.bullish.indexOf(pair) !== -1 ||
                  sd.bullish.indexOf(revPair) !== -1)
              ) {
                signal = "bullish";
              }

              midpoints.push({
                p1: p1,
                p2: p2,
                tp: tp,
                mod90: mod90,
                tMod90: tMod90,
                modality: modality,
                orb: orb,
                signal: signal,
                isTopBear:
                  TOP_BEARISH.indexOf(pair) !== -1 ||
                  TOP_BEARISH.indexOf(revPair) !== -1,
                isTopBull:
                  TOP_BULLISH.indexOf(pair) !== -1 ||
                  TOP_BULLISH.indexOf(revPair) !== -1,
                s1: Astro.getZodiacSign(pos1, isSidereal, transitDateObj),
                s2: Astro.getZodiacSign(pos2, isSidereal, transitDateObj),
                ts: Astro.getZodiacSign(tPos, isSidereal, transitDateObj),
                p1Lon: pos1,
                p2Lon: pos2,
                tLon: tPos,
              });
            }
          });
        }
      }
      return {
        all: midpoints,
        grouped: {
          Cardinal: midpoints.filter(function (m) {
            return m.modality === "Cardinal";
          }),
          Fixed: midpoints.filter(function (m) {
            return m.modality === "Fixed";
          }),
          Mutable: midpoints.filter(function (m) {
            return m.modality === "Mutable";
          }),
        },
        total: midpoints.length,
      };
    },
    [transitPositions, natalPositions, orbFilter, isSidereal, transitDateObj],
  );

  // --- analysisResults の定義が終わった直後に追加 ---

  /**
   * 1. 現在のローカル日時に基づいてトランジット日時を更新
   */
  const handleSetCurrentTime = function () {
    const now = new Date();
    const year = now.getFullYear();
    const month = (now.getMonth() + 1).toString().padStart(2, "0");
    const day = now.getDate().toString().padStart(2, "0");
    setTransitDate(year + "-" + month + "-" + day);

    const hours = now.getHours().toString().padStart(2, "0");
    const minutes = now.getMinutes().toString().padStart(2, "0");
    setTransitTime(hours + ":" + minutes);

    const offset = -now.getTimezoneOffset() / 60;
    setTransitTz(offset);
  };

  /**
   * 2. トランジット日を 1 日戻す
   */
  const handlePrevDay = function () {
    const d = new Date(transitDate);
    d.setDate(d.getDate() - 1);
    const year = d.getFullYear();
    const month = (d.getMonth() + 1).toString().padStart(2, "0");
    const day = d.getDate().toString().padStart(2, "0");
    setTransitDate(year + "-" + month + "-" + day);
  };

  /**
   * 3. トランジット日を 1 日進める
   */
  const handleNextDay = function () {
    const d = new Date(transitDate);
    d.setDate(d.getDate() + 1);
    const year = d.getFullYear();
    const month = (d.getMonth() + 1).toString().padStart(2, "0");
    const day = d.getDate().toString().padStart(2, "0");
    setTransitDate(year + "-" + month + "-" + day);
  };

  // --- この下に handleFileUpload 等の既存コードが続くようにします ---

  /**
   * ファイルアップロード・ハンドラ
   * 5MBのサイズ制限を追加
   */
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const MAX_FILE_SIZE = 5 * 1024 * 1024;
    if (file.size > MAX_FILE_SIZE) {
      alert(
        "ファイルサイズが大きすぎます。5MB以下のファイルを選択してください。",
      );
      e.target.value = "";
      return;
    }

    // パース開始時にローディングを表示
    setIsParsing(true);

    const reader = new FileReader();
    reader.onload = (event) => {
      // 重いパース処理（parseInvestingCSV）が実行される前に、
      // 確実にオーバーレイが描画されるよう、わずかな遅延を入れてUIスレッドに処理を戻します
      setTimeout(function () {
        try {
          if (event && event.target && event.target.result) {
            const parsed = parseInvestingCSV(event.target.result);
            if (parsed && parsed.length > 0) {
              const newId = Date.now().toString();
              const newChart = {
                id: newId,
                name: file.name,
                data: parsed,
              };
              setUploadedCharts(function (prev) {
                return [...prev, newChart];
              });
              setActiveChartId(newId);
            }
          }
        } catch (err) {
          console.error("CSV Parse Error:", err);
          alert("CSVの解析に失敗しました。ファイル形式を確認してください。");
        } finally {
          // 処理完了（成功・失敗問わず）時にローディングを解除
          setIsParsing(false);
        }
      }, 100);
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const deleteChart = (id, e) => {
    e.stopPropagation();
    setUploadedCharts((prev) => {
      const filtered = prev.filter((c) => c.id !== id);
      if (activeChartId === id) {
        setActiveChartId(
          filtered.length > 0 ? filtered[filtered.length - 1].id : null,
        );
      }
      return filtered;
    });
  };

  const handleAddCustomAsset = (asset) => {
    const updated = [...customAssets, asset];
    setCustomAssets(updated);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  };

  /**
   * 重要日一覧から特定の日付を削除するハンドラ
   */
  const handleDeleteImportantDate = function (date) {
    const updated = Object.assign({}, importantDates);
    delete updated[date];
    setImportantDates(updated);
    localStorage.setItem("astronica_important_dates", JSON.stringify(updated));
  };
  const handleDeleteCustomAsset = (id) => {
    const updated = customAssets.filter((a) => a.id !== id);
    setCustomAssets(updated);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    if (selectedAssetId === id) setSelectedAssetId("btc");
  };

  const getAspect = (p1Lon, p2Lon, p1, p2) => {
    if (p1Lon === null || p2Lon === null) return null;
    // 第3引数に定義配列、第6引数に設定ステートを渡します
    return Astro.calculateAspect(
      p1Lon,
      p2Lon,
      ASPECT_DEFINITIONS,
      p1,
      p2,
      aspectSettings,
    );
  };

  const renderDegreeList = () => {
    const planetIds = isHeliocentric
      ? [
        "Earth",
        "Mercury",
        "Venus",
        "Mars",
        "Jupiter",
        "Saturn",
        "Uranus",
        "Neptune",
        "Pluto",
      ]
      : PLANETS.slice(0, 10);

    const extendedPoints = isHeliocentric
      ? []
      : ["NorthNode", "ASC", "DSC", "MC", "IC"];
    const fullList = [...planetIds, ...extendedPoints];

    const formatLon = (lon) => {
      if (lon === null || lon === undefined || isNaN(lon)) {
        return { sym: "—", deg: "0°", rest: "00'00\"" };
      }
      const sign = Astro.getZodiacSign(lon);
      if (!sign || !sign.symbol) {
        return { sym: "?", deg: "0°", rest: "00'00\"" };
      }
      const degInSign = lon % 30;
      const d = Math.floor(degInSign);
      const m = Math.floor((degInSign - d) * 60);
      const s = Math.floor(((degInSign - d) * 60 - m) * 60);
      return {
        sym: sign.symbol,
        deg: `${d}°`,
        rest: `${m.toString().padStart(2, "0")}'${s.toString().padStart(2, "0")}"`,
      };
    };

    /**
     * クリップボードへのコピー処理
     */
    const handleCopyPositions = (title, positions) => {
      const signNames = [
        "Aries",
        "Taurus",
        "Gemini",
        "Cancer",
        "Leo",
        "Virgo",
        "Libra",
        "Scorpio",
        "Sagittarius",
        "Capricorn",
        "Aquarius",
        "Pisces",
      ];

      let headerStr = "";
      if (title === "Birth") {
        headerStr = `${selectedAsset.date.replace(/-/g, "/")}   ${selectedAsset.time} UTC${selectedAsset.timezone >= 0 ? "+" : ""}${selectedAsset.timezone} Birth`;
      } else {
        headerStr = `${transitDate.replace(/-/g, "/")}   ${transitTime} UTC${transitTz >= 0 ? "+" : ""}${transitTz} Transit`;
      }

      const rows = fullList
        .map((id) => {
          const lon = positions[id];
          if (lon === undefined || lon === null) return null;

          const signIdx = Math.floor(lon / 30) % 12;
          const signName = signNames[signIdx];

          const degInSign = lon % 30;
          const d = Math.floor(degInSign);
          const m = Math.floor((degInSign - d) * 60);
          const s = Math.floor(((degInSign - d) * 60 - m) * 60);

          const name = id === "NorthNode" ? "Node" : id;
          // 指定された特殊記号（’ と ″）とフォーマットを忠実に再現
          let degPart = `${d}°${m.toString().padStart(2, "0")}’`;
          if (title === "Transit" && isRetro(id)) {
            degPart += `,R’`;
          }
          degPart += `${s.toString().padStart(2, "0")}″`;

          return `${name},${signName},${degPart}`;
        })
        .filter(Boolean);

      const fullText = [headerStr, "", ...rows].join("\n");

      navigator.clipboard
        .writeText(fullText)
        .then(() => {
          alert(`${title}の度数データをコピーしました。`);
        })
        .catch((err) => {
          console.error("Copy failed:", err);
        });
    };

    const renderColumn = (title, positions, colorTheme) => (
      <div
        style={{
          flex: 1,
          width: "100%",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            textAlign: "center",
            fontSize: "10px",
            fontWeight: "bold",
            color: isDarkMode ? "rgba(255,255,255,0.4)" : "#aaa",
            marginBottom: "8px",
            letterSpacing: "0.1em",
          }}
        >
          {title.toUpperCase()}
        </div>
        <div
          style={{
            background: isDarkMode ? "rgba(255, 255, 255, 0.01)" : "#fff",
            borderRadius: "12px",
            border: isDarkMode
              ? "1px solid rgba(255, 255, 255, 0.05)"
              : "1px solid #f0f0f0",
            padding: "10px",
            overflow: "visible",
            flex: 1,
          }}
        >
          {fullList.map((id) => {
            const pos = positions[id];
            if (pos === undefined) return null;
            const data = formatLon(pos);
            const symbol =
              PLANET_SYMBOLS[id] ||
              (id === "Earth"
                ? "⊕"
                : id.toLowerCase().includes("node")
                  ? "☊"
                  : "•");
            return (
              <div
                key={`${title}-${id}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "5px 0",
                  borderBottom: isDarkMode
                    ? "1px solid rgba(255, 255, 255, 0.03)"
                    : "1px solid #f9f9f9",
                  fontSize: "11px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    width: "100px",
                    flexShrink: 0,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "baseline",
                      minWidth: "40px",
                    }}
                  >
                    <span
                      style={{
                        color: planetColors[id] || colorTheme,
                        fontSize: "20px",
                      }}
                    >
                      {symbol}
                    </span>
                    {title === "Transit" && isRetro(id) && (
                      <span
                        style={{
                          fontSize: "10px",
                          color: "#ff4d4f",
                          fontWeight: "900",
                          marginLeft: "1px",
                        }}
                      >
                        Rx
                      </span>
                    )}
                  </div>
                  <span
                    style={{
                      color: isDarkMode ? "rgba(255,255,255,0.6)" : "#888",
                    }}
                  >
                    {id === "NorthNode" ? "Node" : id}
                  </span>
                </div>
                <div
                  style={{
                    color: isDarkMode ? "#ffffff" : "#333",
                    display: "flex",
                    alignItems: "center",
                    fontWeight: "normal",
                  }}
                >
                  <span
                    style={{
                      backgroundColor: "transparent",
                      color: signColors[data.sym] || "#8b5cf6",
                      width: "22px",
                      height: "22px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginRight: "8px",
                      fontSize: "20px",
                      flexShrink: 0,
                    }}
                  >
                    {data.sym + "\uFE0E"}
                  </span>{" "}
                  <span>
                    {data.deg}
                    {data.rest}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
        {/* コピーリンクを追加 */}
        <div
          onClick={() => handleCopyPositions(title, positions)}
          style={{
            marginTop: "12px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "6px",
            fontSize: "12px",
            color: isDarkMode ? "#cbd5e1" : "#64748b",
            cursor: "pointer",
            padding: "8px",
            transition: "opacity 0.2s",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.7")}
          onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
        >
          <Copy size={14} />
          <span>Copy Positions</span>
        </div>
      </div>
    );

    return (
      <div
        className="degree-list-wrapper"
        style={{
          display: "flex",
          gap: "20px",
          marginTop: "10px",
          flexWrap: "wrap",
        }}
      >
        {renderColumn("Birth", natalPositions, "#6366f1")}
        {renderColumn("Transit", transitPositions, "#10b981")}
      </div>
    );
  };

  const renderAspectTable = function () {
    const tablePlanets =
      isHeliocentric === true
        ? [
          "Earth",
          "Mercury",
          "Venus",
          "Mars",
          "Jupiter",
          "Saturn",
          "Uranus",
          "Neptune",
          "Pluto",
        ]
        : PLANETS.slice(0, 10);

    const TIGHT_THRESHOLD = 1.0;

    // 度数計算用の内部ヘルパー（秒まで計算し、フォーマットを他と統一）
    const getDegInfo = function (lon) {
      if (lon === null || lon === undefined)
        return { sym: "", text: "0°00'00\"" };
      const sign = Astro.getZodiacSign(lon);
      const degInSign = lon % 30;
      const d = Math.floor(degInSign);
      const m = Math.floor((degInSign - d) * 60);
      const s = Math.floor(((degInSign - d) * 60 - m) * 60);
      return {
        sym: sign ? sign.symbol : "",
        text:
          d +
          "°" +
          m.toString().padStart(2, "0") +
          "'" +
          s.toString().padStart(2, "0") +
          '"',
      };
    };

    /* --- App.jsx：renderAspectTable の return 部分を以下に差し替え --- */
    return (
      <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
        <table
          style={{
            borderCollapse: "collapse",
            fontSize: "12px",
            width: "100%",
            minWidth: "800px",
          }}
        >
          <thead>
            <tr>
              <th
                style={Object.assign(
                  {},
                  commonStyles.aspectTableCell(false, isDarkMode),
                  {
                    minWidth: "160px",
                    background: isDarkMode
                      ? "rgba(255,255,255,0.05)"
                      : "#f8fafc",
                  },
                )}
              ></th>
              {tablePlanets.map(function (p) {
                return (
                  <th
                    key={p}
                    style={Object.assign(
                      {},
                      commonStyles.aspectTableCell(false, isDarkMode),
                      {
                        color:
                          planetColors[p] || (isDarkMode ? "#fff" : "#333"),
                        background: isDarkMode
                          ? "rgba(255,255,255,0.05)"
                          : "#f8fafc",
                      },
                    )}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "baseline",
                        justifyContent: "center",
                      }}
                    >
                      {PLANET_SYMBOLS[p] || (p === "Earth" ? "⊕" : p[0])}
                      {isRetro(p) && (
                        <span
                          style={{
                            fontSize: "9px",
                            color: "#ff4d4f",
                            marginLeft: "1px",
                            fontWeight: "900",
                          }}
                        >
                          Rx
                        </span>
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {tablePlanets.map(function (p1, i) {
              const pos = transitPositions[p1];
              const degInfo = getDegInfo(pos);

              return (
                <tr key={p1}>
                  {/* 左端の天体ラベル列 */}
                  <td
                    style={Object.assign(
                      {},
                      commonStyles.aspectTableCell(false, isDarkMode),
                      {
                        fontWeight: "bold",
                        padding: "4px 8px",
                        minWidth: "160px",
                        // 左側の列も背景を暗くする
                        background: isDarkMode
                          ? "rgba(255,255,255,0.03)"
                          : "#f8fafc",
                      },
                    )}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        justifyContent: "flex-start",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "baseline",
                          minWidth: "24px",
                        }}
                      >
                        <span
                          style={{
                            fontSize: "18px",
                            color:
                              planetColors[p1] ||
                              (isDarkMode ? "#fff" : "#333"),
                          }}
                        >
                          {PLANET_SYMBOLS[p1] || (p1 === "Earth" ? "⊕" : p1[0])}
                        </span>
                        {isRetro(p1) && (
                          <span
                            style={{
                              fontSize: "8px",
                              color: "#ff4d4f",
                              fontWeight: "900",
                            }}
                          >
                            Rx
                          </span>
                        )}
                      </div>
                      {/* 天体名 */}
                      <span
                        style={{
                          fontSize: "13px",
                          color: isDarkMode ? "#ffffff" : "#64748b",
                          width: "55px",
                          fontWeight: "bold",
                        }}
                      >
                        {p1}
                      </span>
                      {/* 星座アイコン：設定色を反映し \uFE0E で絵文字化を防止 */}
                      <span
                        style={{
                          fontSize: "16px",
                          color: signColors[degInfo.sym] || "#8b5cf6",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        {degInfo.sym + "\uFE0E"}
                      </span>
                      {/* 4. 度数（fontFamily: "monospace" を削除し、ダークモードに対応） */}
                      <span
                        style={{
                          fontSize: "10px",
                          fontWeight: "normal",
                          color: isDarkMode ? "#ffffff" : "#333",
                          // fontFamily: "monospace" を削除することで標準フォントに統一
                        }}
                      >
                        {degInfo.text}
                      </span>
                    </div>
                  </td>
                  {tablePlanets.map(function (p2, j) {
                    const isUpperTriangle = j > i;
                    const asp =
                      isUpperTriangle === true
                        ? getAspect(
                          transitPositions[p1],
                          transitPositions[p2],
                          p1,
                          p2,
                        )
                        : null;

                    let cellBgColor = commonStyles.aspectTableCell(
                      isUpperTriangle,
                      isDarkMode,
                    ).backgroundColor;

                    // アスペクトセルのハイライト色をダークモード用に透過させる
                    if (isUpperTriangle === true && asp !== null) {
                      const isTight = asp.orb <= TIGHT_THRESHOLD;
                      if (asp.type === "Hard") {
                        cellBgColor = isDarkMode
                          ? isTight
                            ? "rgba(231, 76, 60, 0.25)"
                            : "rgba(231, 76, 60, 0.15)"
                          : isTight
                            ? "#e1f5fe"
                            : "#f1faff";
                      } else if (asp.type === "Soft") {
                        cellBgColor = isDarkMode
                          ? isTight
                            ? "rgba(39, 174, 96, 0.25)"
                            : "rgba(39, 174, 96, 0.15)"
                          : isTight
                            ? "#ffebee"
                            : "#fff5f5";
                      }
                    }

                    return (
                      <td
                        key={p1 + "-" + p2}
                        style={Object.assign(
                          {},
                          commonStyles.aspectTableCell(
                            isUpperTriangle,
                            isDarkMode,
                          ),
                          {
                            backgroundColor: cellBgColor,
                            padding: "6px 2px",
                            height: "48px",
                            verticalAlign: "middle",
                          },
                        )}
                      >
                        {isUpperTriangle === true && asp !== null && (
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              gap: "2px",
                            }}
                          >
                            <span
                              style={{
                                color:
                                  asp.type === "Hard" ? "#e74c3c" : "#27ae60",
                                fontSize: "16px",
                                lineHeight: "1",
                              }}
                            >
                              {asp.symbol}
                            </span>
                            <span
                              style={{
                                fontSize: "9px",
                                color: isDarkMode ? "#ffffff" : "#444",
                              }}
                            >
                              {asp.orb.toFixed(1)}°
                            </span>
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  // =============================================================================
  // 1. ダッシュボード用アスペクトリスト描画関数 (一切の省略なし・完全版)
  // =============================================================================
  const renderDashboardAspectList = function () {
    // 削除済みの ASTEROID_LIST を参照せず、ファイル上部で定義済みの ASPECT_POINTS を利用します
    const listBodies = ASPECT_POINTS;
    const aspectResults = [];

    let set1, set2, label1, label2;
    if (chartMode === "transit-natal") {
      set1 = transitPositions;
      label1 = "T";
      set2 = natalPositions;
      label2 = "N";
    } else if (chartMode === "transit") {
      set1 = transitPositions;
      label1 = "T";
      set2 = transitPositions;
      label2 = "T";
    } else {
      set1 = natalPositions;
      label1 = "N";
      set2 = natalPositions;
      label2 = "N";
    }

    if (
      set1 === undefined ||
      set2 === undefined ||
      set1 === null ||
      set2 === null
    )
      return null;

    const formatDMSLocal = function (lon) {
      if (lon === null || lon === undefined) return "0°00'00\"";
      // 剰余を削除し、純粋な合計度数から算出するように修正
      const deg = Math.floor(lon);
      const min = Math.floor((lon - deg) * 60);
      const sec = Math.floor(((lon - deg) * 60 - min) * 60);
      return (
        deg +
        "°" +
        min.toString().padStart(2, "0") +
        "'" +
        sec.toString().padStart(2, "0") +
        '"'
      );
    };

    // --- 軸ペア定数を追加 ---
    const AXIS_PAIRS = [
      ["ASC", "DSC"],
      ["MC", "IC"],
      ["NorthNode", "SouthNode"],
    ];

    listBodies.forEach(function (p1, i) {
      listBodies.forEach(function (p2, j) {
        // 1. 同一天体・感受点同士のアスペクト（例: T-ASC と N-ASC）は表示から除外する
        // これにより、ASC, MC, Node などの「常にコンジャンクション」となるノイズを排除
        if (p1 === p2) return;

        // 2. 重複計算の防止（Transit単体、またはNatal単体モードの場合）
        if (chartMode !== "transit-natal" && j <= i) return;

        const lon1 = set1[p1];
        const lon2 = set2[p2];
        if (
          lon1 === null ||
          lon1 === undefined ||
          lon2 === null ||
          lon2 === undefined
        )
          return;

        const asp = getAspect(lon1, lon2, p1, p2);
        if (asp === null || asp === undefined) return;

        // --- 追加: 軸ペア(ASC-DSC等)のオポジションを排除 ---
        if (
          asp.name === "Opposition" &&
          AXIS_PAIRS.some(function (pair) {
            return pair.indexOf(p1) !== -1 && pair.indexOf(p2) !== -1;
          })
        )
          return;

        const isMaj = isMajor(asp.name);
        if (aspectCategory === "major" && isMaj === false) return;
        if (aspectCategory === "minor" && isMaj === true) return;

        const diff = Math.abs(lon1 - lon2) % 360;
        const dist = diff > 180 ? 360 - diff : diff;

        aspectResults.push(
          Object.assign({}, asp, {
            p1: p1,
            p2: p2,
            label1: label1,
            label2: label2,
            dist: dist,
          }),
        );
      });
    });

    // 修正：対象天体リストを AspectSection と同期（拡張ポイント表示設定に連動させる）
    const getTargetList = function (src) {
      return Object.keys(src).filter(function (p) {
        // ハウス尖点は除外
        if (/^H\d+$/.test(p)) return false;
        if (!isHeliocentric && p === "Earth") return false;

        const isMaj = isHeliocentric
          ? [
            "Earth",
            "Mercury",
            "Venus",
            "Mars",
            "Jupiter",
            "Saturn",
            "Uranus",
            "Neptune",
            "Pluto",
          ].includes(p)
          : [
            "Sun",
            "Moon",
            "Mercury",
            "Venus",
            "Mars",
            "Jupiter",
            "Saturn",
            "Uranus",
            "Neptune",
            "Pluto",
          ].includes(p);

        // props ではなく、App 内の showExtendedPoints ステートを参照
        if (!showExtendedPoints && !isMaj) return false;
        return true;
      });
    };
    // listBodies の再定義
    const planets1 = getTargetList(set1);
    const planets2 = getTargetList(set2);

    aspectResults.sort(function (a, b) {
      return a.orb - b.orb;
    });

    if (aspectResults.length === 0) {
      return (
        <div
          style={{
            textAlign: "center",
            padding: "40px",
            color: "#94a3b8",
            fontSize: "13px",
            fontWeight: "bold",
          }}
        >
          アスペクトなし
        </div>
      );
    }

    // --- 修正：ダッシュボード用アスペクト一覧のモバイル最適化 ---
    const dashboardAspectGrid = isMobile
      ? "minmax(45px, 1fr) 0.8fr minmax(45px, 1fr) 0.4fr 0.6fr 0.4fr"
      : "1.2fr 1.3fr 1.2fr 0.7fr 1fr 0.7fr";

    return (
      <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
        <div
          className="preserved-grid"
          style={{
            // モバイルでは最小幅を 400px に縮小
            minWidth: isMobile ? "380px" : "920px",
            display: "flex",
            flexDirection: "column",
            gap: "1px",
            background: isDarkMode ? "rgba(255,255,255,0.00)" : "#f1f5f9",
          }}
        >
          <div
            className="preserved-grid"
            style={{
              display: "grid",
              gridTemplateColumns: dashboardAspectGrid,
              padding: isMobile ? "10px 4px" : "10px 16px",
              background: isDarkMode ? "rgba(255, 255, 255, 0.08)" : "#f8fafc",
              fontSize: isMobile ? "9px" : "10px",
              fontWeight: "bold",
              color: isDarkMode ? "#ffffff" : "#64748b",
              borderBottom: "1px solid #f0f0f071",
            }}
          >
            <div>FROM</div>
            <div>アスペクト</div>
            <div>TO</div>
            <div style={{ textAlign: "right" }}>角度</div>
            <div style={{ textAlign: "right" }}>ARC</div>
            <div style={{ textAlign: "right" }}>Orb</div>
          </div>

          <div style={{ maxHeight: "450px", overflowY: "auto" }}>
            {aspectResults.map(function (item, idx) {
              var isMajor = function (name) {
                return Astro.ASPECTS.major.some(function (asp) {
                  return asp.name === name;
                });
              };

              var currentLimit = isMajor(item.name)
                ? aspectSettings.majorOrb
                : aspectSettings.minorOrb;
              var isTight = item.orb <= currentLimit * 0.1;

              var sign1 = Astro.getZodiacSign(set1[item.p1]);
              var sign2 = Astro.getZodiacSign(set2[item.p2]);

              return (
                <div
                  key={idx}
                  className="preserved-grid"
                  style={{
                    display: "grid",
                    gridTemplateColumns: dashboardAspectGrid,
                    padding: isMobile ? "8px 4px" : "10px 16px",
                    background: isTight
                      ? isDarkMode
                        ? "rgba(253, 248, 230, 0.1)"
                        : "#f9f9f5"
                      : "transparent",
                    alignItems: "center",
                    fontSize: isMobile ? "11px" : "12px",
                    borderBottom: "1px solid #f0f0f080",
                    color: isDarkMode ? "#ffffff" : "inherit",
                  }}
                >
                  {/* FROM 列: モバイル時は名前を隠してアイコン幅を詰める */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: isMobile
                        ? "10px 32px 0px 20px"
                        : "15px 40px 70px 20px",
                      alignItems: "center",
                      gap: isMobile ? "2px" : "4px",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "9px",
                        color: isDarkMode ? "rgba(255,255,255,0.4)" : "#cbd5e1",
                        fontWeight: "bold",
                      }}
                    >
                      {item.label1}
                    </span>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "baseline",
                        minWidth: "40px",
                      }}
                    >
                      <span
                        style={{
                          fontSize: isMobile ? "20px" : "24px",
                          color:
                            planetColors[item.p1] ||
                            (isDarkMode ? "#fff" : "#333"),
                        }}
                      >
                        {PLANET_SYMBOLS[item.p1]}
                      </span>
                      {/* --- 逆行表示 Rx --- */}
                      {item.label1 === "T" && isRetro(item.p1) && (
                        <span
                          style={{
                            fontSize: "10px",
                            color: "#ff4d4f",
                            fontWeight: "900",
                            marginLeft: "1px",
                          }}
                        >
                          Rx
                        </span>
                      )}
                    </div>
                    {!isMobile && (
                      <span
                        style={{
                          fontWeight: "bold",
                          fontSize: "12px",
                          color: isDarkMode ? "#ffffff" : "rgb(136, 136, 136)",
                        }}
                      >
                        {item.p1}
                      </span>
                    )}
                    {isMobile && <div />}
                    <span
                      style={{
                        fontSize: isMobile ? "16px" : "16px",
                        color: signColors[sign1.symbol] || "#8b5cf6",
                        textAlign: "center",
                      }}
                    >
                      {sign1.symbol + "\uFE0E"}
                    </span>
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: isMobile ? "column" : "row",
                        alignItems: "center",
                        gap: isMobile ? "0px" : "4px",
                      }}
                    >
                      <span
                        style={{
                          fontSize: isMobile ? "16px" : "20px",
                          color: item.type === "Hard" ? "#e74c3c" : "#3498db",
                        }}
                      >
                        {item.symbol}
                      </span>
                      <span
                        style={{
                          fontWeight: "bold",
                          fontSize: isMobile ? "10px" : "11px",
                          color: "#888888", // アスペクト名のフォント色を修正
                        }}
                      >
                        {item.name}
                        {item.name === "Square" && (
                          <span
                            style={{
                              fontWeight: "normal",
                              fontSize: "12px",
                              marginLeft: "2px",
                            }}
                          >
                            {getSquareLabel(
                              item.p1,
                              set1[item.p1],
                              item.p2,
                              set2[item.p2],
                            )}
                          </span>
                        )}
                      </span>
                    </div>
                  </div>

                  {/* TO 列 */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: isMobile
                        ? "10px 32px 0px 20px"
                        : "15px 40px 70px 20px",
                      alignItems: "center",
                      gap: isMobile ? "2px" : "4px",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "9px",
                        color: isDarkMode ? "rgba(255,255,255,0.4)" : "#cbd5e1",
                        fontWeight: "bold",
                      }}
                    >
                      {item.label2}
                    </span>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "baseline",
                        minWidth: "40px",
                      }}
                    >
                      <span
                        style={{
                          fontSize: isMobile ? "20px" : "24px",
                          color:
                            planetColors[item.p2] ||
                            (isDarkMode ? "#fff" : "#333"),
                        }}
                      >
                        {PLANET_SYMBOLS[item.p2]}
                      </span>
                      {/* --- 追加: TO側がトランジット天体かつ逆行中なら Rx を表示 --- */}
                      {item.label2 === "T" && isRetro(item.p2) && (
                        <span
                          style={{
                            fontSize: "10px",
                            color: "#ff4d4f",
                            fontWeight: "900",
                            marginLeft: "1px",
                          }}
                        >
                          Rx
                        </span>
                      )}
                    </div>
                    {!isMobile && (
                      <span
                        style={{
                          fontWeight: "bold",
                          fontSize: "12px",
                          color: isDarkMode ? "#ffffff" : "rgb(136, 136, 136)",
                        }}
                      >
                        {item.p2}
                      </span>
                    )}
                    {isMobile && <div />}
                    <span
                      style={{
                        fontSize: isMobile ? "16px" : "16px",
                        color: signColors[sign2.symbol] || "#8b5cf6",
                        textAlign: "center",
                      }}
                    >
                      {sign2.symbol + "\uFE0E"}
                    </span>
                  </div>

                  {/* 数値列 */}
                  <div
                    style={{
                      textAlign: "right",
                      fontSize: isMobile ? "10px" : "11px",
                      color: isDarkMode ? "#ffffff" : "#64748b",
                    }}
                  >
                    {item.angle}°
                  </div>
                  <div
                    style={{
                      textAlign: "right",
                      fontSize: isMobile ? "10px" : "11px",
                      color: isDarkMode ? "#ffffff" : "#64748b",
                    }}
                  >
                    {formatDMSLocal(item.dist)}
                  </div>
                  <div
                    style={{
                      textAlign: "right",
                      fontSize: isMobile ? "10px" : "11px",
                      fontWeight: isTight ? "bold" : "normal",
                      color: isDarkMode ? "#ffffff" : "#1e293b",
                    }}
                  >
                    {item.orb.toFixed(2)}°
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  /**
   * 【修正後コード】renderDignityScoring 内のヘッダーセクション
   */
  const renderDignityScoring = function () {
    if (isHeliocentric || !transitPositions) return null;

    const tablePlanets = PLANETS.slice(0, 10);
    let totalScore = 0;

    const items = tablePlanets
      .map(function (p) {
        const lon = transitPositions[p];
        if (lon === null || lon === undefined) return null;
        const sign = Astro.getZodiacSign(lon);
        const dignity = Astro.getDignity(p, sign.name);
        if (dignity) totalScore += dignity.score;
        return { id: p, sign: sign, dignity: dignity };
      })
      .filter(function (x) {
        return x !== null;
      });

    return (
      <div style={{ gridColumn: "span 12" }}>
        <div style={commonStyles.dynamicBlockCard(isDarkMode, isMobile)}>
          {/* --- 修正ポイント：モバイル時に垂直スタック化 (f100.jpg対応) --- */}
          <div
            style={Object.assign(
              {},
              commonStyles.dynamicBlockTitle(isDarkMode),
              {
                flexDirection: isMobile ? "column" : "row",
                alignItems: isMobile ? "flex-start" : "center",
                justifyContent: "space-between",
                paddingBottom: "15px",
                gap: isMobile ? "15px" : "10px",
              },
            )}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <Target size={18} color="#f1c40f" />
              <span style={{ lineHeight: "1.4" }}>
                エッセンシャル・ディグニティ・スコアリング
              </span>
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "10px",
                padding: "8px 16px",
                borderRadius: "8px",
                width: isMobile ? "100%" : "auto", // モバイル時は全幅
                background:
                  totalScore >= 0
                    ? "rgba(39, 174, 96, 0.1)"
                    : "rgba(231, 76, 60, 0.1)",
                border:
                  "1px solid " + (totalScore >= 0 ? "#27ae6033" : "#e74c3c33"),
                boxSizing: "border-box",
              }}
            >
              <span
                style={{
                  fontSize: "11px",
                  fontWeight: "bold",
                  color: isDarkMode ? "#aaa" : "#64748b",
                }}
              >
                TOTAL DIGNITY:
              </span>
              <span
                style={{
                  fontSize: "20px",
                  fontWeight: "900",
                  color: totalScore >= 0 ? "#27ae60" : "#e74c3c",
                }}
              >
                {totalScore > 0 ? "+" : ""}
                {totalScore}
              </span>
            </div>
          </div>

          {/* ... カード内部の grid 描画処理 ... */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile
                ? "1fr 1fr"
                : "repeat(auto-fill, minmax(185px, 1fr))",
              gap: isMobile ? "10px" : "24px", // 修正
              padding: isMobile ? "10px" : "24px", // 修正
            }}
          >
            {items.map(function (item) {
              /* 既存の惑星スコアカード描画ロジック */
              const color =
                planetColors[item.id] || (isDarkMode ? "#fff" : "#333");
              const d = item.dignity;
              const hasDignity = d && d.score !== 0;
              return (
                <div
                  key={item.id}
                  style={{
                    padding: "12px",
                    borderRadius: "12px",
                    background: isDarkMode
                      ? "rgba(255, 255, 255, 0.02)"
                      : "#fcfcfc",
                    border:
                      "1px solid " +
                      (isDarkMode ? "rgba(255, 255, 255, 0.05)" : "#f1f5f9"),
                    display: "flex",
                    flexDirection: "column",
                    gap: "6px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <span style={{ fontSize: "20px", color: color }}>
                        {PLANET_SYMBOLS[item.id]}
                      </span>
                      <span
                        style={{
                          fontSize: "13px",
                          fontWeight: "bold",
                          color: isDarkMode ? "#fff" : "#475569",
                        }}
                      >
                        {item.id}
                      </span>
                    </div>
                    {hasDignity && (
                      <div
                        style={{
                          fontSize: "11px",
                          fontWeight: "900",
                          padding: "2px 6px",
                          borderRadius: "4px",
                          background:
                            d.score > 0
                              ? "rgba(39, 174, 96, 0.1)"
                              : "rgba(231, 76, 60, 0.1)",
                          color: d.score > 0 ? "#27ae60" : "#e74c3c",
                        }}
                      >
                        {d.score > 0 ? "+" : ""}
                        {d.score}
                      </div>
                    )}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      fontSize: "11px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        color: isDarkMode ? "#9ca3af" : "#64748b",
                      }}
                    >
                      <span
                        style={{
                          color: signColors[item.sign.symbol] || "#8b5cf6",
                          fontSize: "14px",
                        }}
                      >
                        {item.sign.symbol + "\uFE0E"}
                      </span>
                      <span>{item.sign.name.substring(0, 3)}</span>
                    </div>
                    <span
                      style={{
                        fontSize: "10px",
                        color: hasDignity
                          ? d.score > 0
                            ? "#27ae60"
                            : "#e74c3c"
                          : isDarkMode
                            ? "rgba(255, 255, 255, 0.4)"
                            : "#cbd5e1",
                      }}
                    >
                      {d ? d.label.toUpperCase() : "PEREGRINE"}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // ============================================================
  // 1. ダッシュボード表示の定義 (設定アイコン・動的な色対応版)
  // ============================================================
  const renderDashboard = () => {
    // サイドバーの幅を定義
    const sidebarWidth = sidebarCollapsed ? "70px" : "280px";

    const dashboardHeader = (
      <Header
        isTrialActive={isTrialActive}
        selectedAsset={selectedAssetId}
        setSelectedAsset={setSelectedAssetId}
        transitDate={transitDate}
        setTransitDate={setTransitDate}
        transitTime={transitTime}
        setTransitTime={setTransitTime}
        timeZone={"UTC" + (transitTz >= 0 ? "+" : "") + transitTz}
        setTimeZone={function (val) {
          const num = parseInt(val.replace("UTC", ""), 10);
          if (!isNaN(num)) setTransitTz(num);
        }}
        isGeocentric={!isHeliocentric}
        setIsGeocentric={function (val) {
          setIsHeliocentric(!val);
        }}
        isTropical={!isSidereal}
        setIsTropical={function (val) {
          setIsSidereal(!val);
        }}
        assets={allAssets}
        onFinish={function () {
          if (window.confirm("セッションを終了しますか？")) {
            window.location.reload();
          }
        }}
        onSetCurrentTime={handleSetCurrentTime}
        onPrevDay={handlePrevDay}
        onNextDay={handleNextDay}
        commonStyles={commonStyles}
        onSettingsClick={function () {
          setActiveSection("settings");
        }}
        isDarkMode={isDarkMode}
        setIsDarkMode={setIsDarkMode}
        sidebarCollapsed={sidebarCollapsed}
        setSidebarCollapsed={setSidebarCollapsed}
        /* 追加：現在のサイドバー幅を渡してヘッダーのpaddingを調整する */
        sidebarWidth={isMobile ? "0px" : sidebarWidth}
      />
    );

    // ============================================================
    // 2. メインコンテンツの定義 (一切の省略なし・完全版)
    // ============================================================
    const mainContent = (
      <div style={{ padding: isMobile === true ? "70px 0 0 0" : "24px" }}>
        {activeSection === "dashboard" ? (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(12, 1fr)",
              gap: "20px",
            }}
          >
            {/* ホロスコープチャートセクション */}
            <div
              style={Object.assign(
                {},
                commonStyles.dynamicBlockCard(isDarkMode, isMobile),
                {
                  gridColumn: isMobile === true ? "span 12" : "span 7",
                },
              )}
            >
              <div style={commonStyles.dynamicBlockTitle(isDarkMode)}>
                <Globe size={18} /> <span>ホロスコープ</span>
              </div>
              <HoroscopeChart
                transitPositions={transitPositions}
                natalPositions={natalPositions}
                isHeliocentric={isHeliocentric}
                assetName={selectedAsset.name}
                isSidereal={isSidereal}
                chartMode={chartMode}
                setChartMode={setChartMode}
                aspectCategory={aspectCategory}
                setAspectCategory={setAspectCategory}
                windowWidth={windowWidth}
                birthLabel={
                  chartMode === "transit"
                    ? null // トランジットモード時はラベルを空にして非表示にする
                    : selectedAsset.date.replace(/-/g, "/") +
                    " " +
                    selectedAsset.time +
                    " UTC" +
                    (selectedAsset.timezone >= 0 ? "+" : "") +
                    selectedAsset.timezone
                }
                transitLabel={
                  transitDate.replace(/-/g, "/") +
                  " " +
                  transitTime +
                  " UTC" +
                  (transitTz >= 0 ? "+" : "") +
                  transitTz
                }
                planetColors={planetColors}
                signColors={signColors}
                horoscopeStyle={horoscopeStyle}
                hideAspectLines={hideAspectLines}
                showExtendedPoints={showExtendedPoints}
                isDarkMode={isDarkMode}
              />
            </div>
            {/* 惑星度数リスト & 重要日セクション */}
            <div
              style={{
                gridColumn: isMobile === true ? "span 12" : "span 5",
                display: "flex",
                flexDirection: "column",
                gap: "20px",
                fontWeight: "bold",
              }}
            >
              <div style={commonStyles.dynamicBlockCard(isDarkMode, isMobile)}>
                <div style={commonStyles.dynamicBlockTitle(isDarkMode)}>
                  <Activity size={18} /> <span>Birth / Transit 度数</span>
                </div>
                {renderDegreeList()}
              </div>
              <div style={commonStyles.dynamicBlockCard(isDarkMode, isMobile)}>
                <div style={commonStyles.dynamicBlockTitle(isDarkMode)}>
                  <Target size={18} /> <span>重要日一覧</span>
                </div>
                <div style={commonStyles.importantDatesBox}>
                  {Object.keys(importantDates).length > 0 ? (
                    Object.keys(importantDates)
                      // 日付文字列を比較して古い順（昇順）に並び替え
                      .sort(function (a, b) {
                        return new Date(a).getTime() - new Date(b).getTime();
                      })
                      .map(function (d) {
                        return (
                          <div
                            key={d}
                            style={Object.assign({}, commonStyles.dateBadge, {
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: "10px",
                              marginBottom: "4px",
                            })}
                          >
                            <span>{d}</span>
                            <X
                              size={14}
                              style={{
                                cursor: "pointer",
                                opacity: 0.6,
                                flexShrink: 0,
                              }}
                              onClick={function () {
                                if (
                                  window.confirm(
                                    d + " を重要日から削除しますか？",
                                  )
                                ) {
                                  handleDeleteImportantDate(d);
                                }
                              }}
                            />
                          </div>
                        );
                      })
                  ) : (
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#999",
                        padding: "10px",
                      }}
                    >
                      登録なし
                    </div>
                  )}
                </div>
              </div>
            </div>


            {/* 新設：仮想通貨チャートブロック（横幅100%で追加） */}
            <div style={{ gridColumn: "span 12" }}>
              <CryptoChartBlock
                selectedAssetId={selectedAssetId}
                isDarkMode={isDarkMode}
                isMobile={isMobile}
                commonStyles={commonStyles}
              />
            </div>


            {/* 新設：本日の主要イベント */}
            <DailyEventsBlock
              transitDate={transitDateObj}
              isHeliocentric={isHeliocentric}
              isSidereal={isSidereal}
              planetColors={planetColors}
              signColors={signColors}
              aspectSettings={aspectSettings}
              isDarkMode={isDarkMode}
              isMobile={isMobile}
              transitTz={transitTz}
            />

            {/* 新設：ディグニティ・スコアリングセクション */}
            {renderDignityScoring()}

            {/* アスペクト一覧セクション (プルダウン2種追加・デザイン更新) */}

            <div style={{ gridColumn: "span 12" }}>
              <div style={commonStyles.dynamicBlockCard(isDarkMode, isMobile)}>
                <div
                  className="aspect-header-mobile"
                  style={Object.assign(
                    {},
                    commonStyles.dynamicBlockTitle(isDarkMode),
                    {
                      flexDirection: isMobile ? "column" : "row",
                      alignItems: isMobile ? "flex-start" : "center",
                      justifyContent: "space-between",
                      paddingBottom: "15px",
                      gap: isMobile ? "15px" : "10px",
                    },
                  )}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <Zap size={18} /> <span>アスペクト一覧</span>
                  </div>

                  {/* --- 修正ポイント：プルダウンを垂直に積み、横幅100%に --- */}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: isMobile ? "column" : "row",
                      gap: "10px",
                      width: isMobile ? "100%" : "auto",
                      justifyContent: "flex-end",
                    }}
                  >
                    <select
                      value={chartMode}
                      onChange={function (e) {
                        setChartMode(e.target.value);
                      }}
                      style={{
                        width: isMobile ? "100%" : "140px", // モバイル時は全幅
                        fontSize: "12px",
                        padding: "8px 16px",
                        borderRadius: "20px",
                        border: isDarkMode
                          ? "1px solid rgba(255,255,255,0.1)"
                          : "1px solid #ddd",
                        background: isDarkMode ? "#2d2c33" : "#fff",
                        color: isDarkMode ? "#fff" : "#333",
                        outline: "none",
                        colorScheme: isDarkMode ? "dark" : "light",
                        cursor: "pointer",
                        fontWeight: "bold",
                      }}
                    >
                      <option value="transit">トランジット</option>
                      <option value="transit-natal">
                        ネイタル × トランジット
                      </option>
                      <option value="natal">ネイタル</option>
                    </select>

                    <select
                      value={aspectCategory}
                      onChange={function (e) {
                        setAspectCategory(e.target.value);
                      }}
                      style={{
                        width: isMobile ? "100%" : "140px", // モバイル時は全幅
                        fontSize: "12px",
                        padding: "8px 16px",
                        borderRadius: "20px",
                        border: isDarkMode
                          ? "1px solid rgba(255,255,255,0.1)"
                          : "1px solid #ddd",
                        background: isDarkMode ? "#2d2c33" : "#fff",
                        color: isDarkMode ? "#fff" : "#333",
                        outline: "none",
                        colorScheme: isDarkMode ? "dark" : "light",
                        cursor: "pointer",
                        fontWeight: "bold",
                      }}
                    >
                      <option value="all">すべて表示</option>
                      <option value="major">主要アスペクト</option>
                      <option value="hard">ハードアスペクト</option>
                      <option value="soft">ソフトアスペクト</option>
                      <option value="minor">マイナーアスペクト</option>
                    </select>
                  </div>
                </div>
                {renderDashboardAspectList()}
              </div>
            </div>
            {/* 惑星詳細トランジット情報セクション */}
            <div style={{ gridColumn: "span 12" }}>
              {/* 他のブロックと統一したカードスタイルを適用 */}
              <div style={commonStyles.dynamicBlockCard(isDarkMode, isMobile)}>
                {/* styles.jsで定義した見出しスタイル（ダークモードで白くなる設定）を適用 */}
                <div style={commonStyles.dynamicBlockTitle(isDarkMode)}>
                  <Globe size={18} /> <span>惑星詳細情報 (トランジット)</span>
                </div>
                <PlanetInfoTransit
                  transitPositions={transitPositions}
                  transitDate={transitDateObj}
                  isHeliocentric={isHeliocentric}
                  isRetro={isRetro}
                  planetColors={planetColors}
                  signColors={signColors}
                  aspectSettings={aspectSettings}
                  isDarkMode={isDarkMode}
                  isMobile={isMobile}
                  houseSystem={houseSystem} // ★追加：ハウスシステム設定を渡す
                />
              </div>
            </div>
            {/* アスペクトテーブルセクション */}
            <div
              style={Object.assign(
                {},
                commonStyles.dynamicBlockCard(isDarkMode, isMobile),
                {
                  gridColumn: "span 12",
                },
              )}
            >
              <div style={commonStyles.dynamicBlockTitle(isDarkMode)}>
                <Zap size={18} />{" "}
                <span>
                  アスペクト・グリッド (
                  {isHeliocentric === true ? "Heliocentric" : "Geocentric"})
                </span>
              </div>
              <div style={{ padding: "10px 0" }}>{renderAspectTable()}</div>
            </div>
          </div>
        ) : activeSection === "settings" ? (
          /* アプリ設定画面：すべてのプロップスを確実に渡す */
          <div style={commonStyles.dynamicBlockCard(isDarkMode, isMobile)}>
            <div style={commonStyles.dynamicBlockTitle(isDarkMode)}>
              <ArrowLeft
                size={20}
                style={{ cursor: "pointer", color: "#666", marginRight: "5px" }}
                onClick={function () {
                  setActiveSection("dashboard");
                }}
              />
              <span>アプリ設定</span>
            </div>
            <SettingsSection
              // 1. カラー・アスペクト設定
              planetColors={planetColors}
              setPlanetColors={setPlanetColors}
              signColors={signColors}
              setSignColors={setSignColors}
              onReset={handleResetColors}
              aspectSettings={aspectSettings}
              onAspectSettingsChange={handleSaveAspectSettings}
              // 2. ホロスコープ表示設定
              horoscopeStyle={horoscopeStyle}
              onHoroscopeStyleChange={handleHoroscopeStyleChange}
              hideAspectLines={hideAspectLines}
              onHideAspectLinesChange={handleHideAspectLinesChange}
              // 3. 銘柄・テーマ設定
              isDarkMode={isDarkMode}
              assets={allAssets}
              onAddCustom={handleAddCustomAsset}
              onDeleteCustom={handleDeleteCustomAsset}
              setTransitTz={setTransitTz}
              // 4. 観測地・計算設定 (props 名が漏れていた部分を修正)
              observerLat={observerLat}
              setObserverLat={setObserverLat}
              observerLon={observerLon}
              setObserverLon={setObserverLon}
              isCustomLocation={isCustomLocation}
              setIsCustomLocation={setIsCustomLocation}
              houseSystem={houseSystem}
              setHouseSystem={setHouseSystem}
            />
          </div>
        ) : (
          /* 個別分析画面（ミッドポイント、カレンダー等すべて含む） */
          <div style={commonStyles.dynamicBlockCard(isDarkMode, isMobile)}>
            <div style={commonStyles.dynamicBlockTitle(isDarkMode)}>
              <ArrowLeft
                size={20}
                className="no-print"
                style={{ cursor: "pointer", color: "#666", marginRight: "5px" }}
                onClick={function () {
                  setActiveSection("dashboard");
                }}
              />
              <span>
                {activeSection === "aspects" && "アスペクト分析"}
                {activeSection === "midpoint" && "ミッドポイント分析"}
                {activeSection === "solar_arc_midpoint" && "ソーラーアーク・ディレクション"}
                {activeSection === "chart_analysis" && "チャート分析"}
                {activeSection === "moon" && "月相分析"}
                {activeSection === "planet_return" && "惑星リターン分析"}
                {activeSection === "declination" && "赤緯分析"}
                {activeSection === "calendar" && "天体イベントカレンダー"}
                {activeSection === "speed" && "惑星スピード"}
                {activeSection === "aspect_search" &&
                  "アスペクト検索・統計分析"}
              </span>
            </div>

            {/* 各セクションの呼び出し（色設定の受け渡しを徹底） */}
            {activeSection === "aspects" && (
              <AspectSection
                positions={transitPositions}
                natalPositions={natalPositions}
                showExtendedPoints={showExtendedPoints}
                transitDate={transitDateObj}
                isHeliocentric={isHeliocentric}
                isSidereal={isSidereal}
                isRetro={isRetro}
                planetColors={planetColors}
                signColors={signColors}
                aspectSettings={aspectSettings}
                isDarkMode={isDarkMode}
                isMobile={isMobile}
              />
            )}

            {activeSection === "midpoint" && (
              <Dial90Chart
                midpoints={
                  analysisResults !== null && analysisResults.all !== undefined
                    ? analysisResults.all
                    : []
                }
                transitPositions={transitPositions}
                natalPositions={natalPositions}
                isHeliocentric={isHeliocentric}
                isSidereal={isSidereal}
                transitDateObj={transitDateObj}
                natalDateTime={
                  selectedAsset.date.replace(/-/g, "/") +
                  " " +
                  selectedAsset.time +
                  " UTC" +
                  (selectedAsset.timezone >= 0 ? "+" : "") +
                  selectedAsset.timezone
                }
                assetName={selectedAsset.name}
                transitDateTime={
                  transitDate.replace(/-/g, "/") +
                  " " +
                  transitTime +
                  " UTC" +
                  (transitTz >= 0 ? "+" : "") +
                  transitTz
                }
                planetColors={planetColors}
                signColors={signColors}
                isDarkMode={isDarkMode}
                isMobile={isMobile}
                // --- 新規定伝播 ---
                selectedTriggers={selectedTriggers}
                selectedNatalBodies={selectedNatalBodies}
                orbFilter={orbFilter}
                userLocation={userLocation}
                natalDateObj={natalDateObj}
              />
            )}

            {activeSection === "solar_arc_midpoint" && (
              <SolarArcMidpointDial
                natalPositions={natalPositions}
                natalDate={natalDateObj}
                transitDate={transitDateObj}
                isSidereal={isSidereal}
                planetColors={planetColors}
                signColors={signColors}
                isDarkMode={isDarkMode}
                isMobile={isMobile}
                selectedAsset={selectedAsset}
                transitTz={transitTz}
                userLocation={userLocation}
              />
            )}

            {activeSection === "chart_analysis" && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "20px",
                }}
              >
                <div
                  style={Object.assign(
                    {},
                    commonStyles.dynamicBlockCard(isDarkMode, isMobile),
                    {
                      padding: isMobile ? "10px" : "24px",
                      minHeight: isMobile ? "600px" : "900px",
                    },
                  )}
                >
                  {/* 修正：モバイルでも表示するために条件分岐を削除 */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: isMobile ? "flex-start" : "space-between",
                      alignItems: isMobile ? "stretch" : "flex-start",
                      flexDirection: isMobile ? "column" : "row",
                      marginBottom: "20px",
                      flexWrap: "wrap",
                      gap: "15px",
                      width: "100%",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                        width: isMobile ? "100%" : "auto",
                      }}
                    >
                      <div
                        style={commonStyles.dynamicBlockTitle(
                          isDarkMode,
                          isMobile,
                        )}
                      >
                        <TrendingUp size={20} /> <span>登録済み銘柄データ</span>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          flexDirection: isMobile ? "column" : "row",
                          gap: "8px",
                          marginTop: "10px",
                          width: "100%",
                        }}
                      >
                        {uploadedCharts.map(function (chart) {
                          return (
                            <div
                              key={chart.id}
                              onClick={function () {
                                setActiveChartId(chart.id);
                              }}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: isMobile
                                  ? "space-between"
                                  : "center",
                                gap: "8px",
                                padding: "8px 16px",
                                width: isMobile ? "100%" : "auto",
                                boxSizing: "border-box",
                                background:
                                  activeChartId === chart.id
                                    ? "#2c2d96"
                                    : "#fff",
                                color:
                                  activeChartId === chart.id
                                    ? "#fff"
                                    : "#64748b",
                                border:
                                  "1px solid " +
                                  (activeChartId === chart.id
                                    ? "#2c2d96"
                                    : "#e2e8f0"),
                                borderRadius: "20px",
                                fontSize: "12px",
                                cursor: "pointer",
                                fontWeight: "bold",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                }}
                              >
                                <FileText size={14} />
                                <span>{chart.name}</span>
                              </div>
                              <X
                                size={14}
                                style={{ marginLeft: "4px", opacity: 0.7 }}
                                onClick={function (e) {
                                  deleteChart(chart.id, e);
                                }}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "8px",
                        padding: "12px 24px",
                        width: isMobile ? "100%" : "auto",
                        boxSizing: "border-box",
                        background:
                          "linear-gradient(45deg, #2c2d96 0%, #9c2393 100%)",
                        color: "#fff",
                        borderRadius: "10px",
                        cursor: "pointer",
                        fontWeight: "bold",
                        fontSize: "13px",
                      }}
                    >
                      <Upload size={16} /> 銘柄を追加 (CSV)
                      <input
                        type="file"
                        accept=".csv"
                        onChange={handleFileUpload}
                        style={{ display: "none" }}
                      />
                    </label>
                  </div>

                  <ChartAnalysisGate
                    key={activeChartId || "gate-root"}
                    activeChartId={activeChartId}
                    csvData={currentUploadedData}
                    uploadedCharts={uploadedCharts}
                    observerLat={observerLat}
                    observerLon={observerLon}
                    aspectSettings={aspectSettings}
                    planetColors={planetColors}
                    isDarkMode={isDarkMode}
                    natalPositions={natalPositions}
                    onClose={function () {
                      setActiveChartId(null);
                    }}
                  />
                </div>
              </div>
            )}

            {activeSection === "moon" && (
              <MoonStateSection
                transitDate={transitDateObj}
                isHeliocentric={isHeliocentric}
                isSidereal={isSidereal}
                planetColors={planetColors}
                signColors={signColors}
                isDarkMode={isDarkMode}
                isMobile={isMobile}
              />
            )}

            {activeSection === "ephemeris_chart" && (
              <EphemerisChartSection
                positions={transitPositions}
                transitDate={transitDateObj} // ★日付オブジェクトを追加
                isDarkMode={isDarkMode} // ★ダークモードフラグを追加
                planetColors={planetColors}
                signColors={signColors}
                isHeliocentric={isHeliocentric}
                isSidereal={isSidereal}
                isRetro={isRetro}
                aspectSettings={aspectSettings}
                isMobile={isMobile}
              />
            )}

            {activeSection === "planet_return" && (
              <PlanetReturnSection
                natalDate={natalDateObj}
                natalPositions={natalPositions}
                currentPositions={transitPositions}
                transitDate={transitDateObj}
                isSidereal={isSidereal}
                planetColors={planetColors}
                isDarkMode={isDarkMode} // ★ダークモードフラグを追加
                isMobile={isMobile}
              />
            )}
            {activeSection === "declination" && (
              <DeclinationAspectsSection
                date={transitDateObj}
                isSidereal={isSidereal}
                planetColors={planetColors}
                signColors={signColors}
                isDarkMode={isDarkMode}
                isMobile={isMobile}
              />
            )}
            {activeSection === "calendar" && (
              <CelestialEventsSection
                baseDate={transitDateObj}
                isHeliocentric={isHeliocentric}
                isSidereal={isSidereal}
                planetColors={planetColors}
                signColors={signColors}
                isDarkMode={isDarkMode}
                isMobile={isMobile}
              />
            )}

            {activeSection === "speed" && (
              <RetrogradeSpeedSection
                date={transitDateObj}
                positions={transitPositions}
                isSidereal={isSidereal}
                planetColors={planetColors}
                signColors={signColors}
                isDarkMode={isDarkMode}
                isMobile={isMobile}
              />
            )}
            {activeSection === "aspect_search" && (
              <AspectSearchSection
                natalPositions={natalPositions}
                isHeliocentric={isHeliocentric}
                isSidereal={isSidereal}
                planetColors={planetColors}
                signColors={signColors}
                isDarkMode={isDarkMode}
                isMobile={isMobile}
              />
            )}
            {activeSection === "astrology_report" && (
              <AstrologyReportSection
                natalPositions={natalPositions}
                natalDate={natalDateObj}
                transitDate={transitDateObj}
                isSidereal={isSidereal}
                planetColors={planetColors}
                signColors={signColors}
                isDarkMode={isDarkMode}
                isMobile={isMobile}
                assetName={selectedAsset.name}
                birthLabel={`${selectedAsset.date}  ${selectedAsset.time} UTC${selectedAsset.timezone >= 0 ? "+" + selectedAsset.timezone : selectedAsset.timezone}`}
                transitLabel={`${transitDate.replace(/-/g, "/")} ${transitTime} UTC${transitTz >= 0 ? "+" + transitTz : transitTz}`}
                onNavigate={setActiveSection}
              />
            )}
          </div>
        )}
      </div>
    );

    return (
      <div style={commonStyles.dashboardWrapper} className="dashboard-wrapper">
        {isMobile && sidebarOpen && (
          <div
            onClick={function () {
              setSidebarOpen(false);
            }}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.5)",
              zIndex: 1100,
            }}
          />
        )}
        <aside
          style={{
            ...commonStyles.sidebar,
            position: "fixed",
            left: 0,
            top: 0,
            height: "100vh",
            zIndex: 1200,
            width: isMobile ? "280px" : sidebarWidth,
            background: isDarkMode
              ? "#1a191e"
              : "linear-gradient(-45deg, #2c2d96, #9c2393)",
            borderRight: isDarkMode
              ? "1px solid rgba(255,255,255,0.05)"
              : "none",
            transform:
              isMobile && !sidebarOpen ? "translateX(-100%)" : "translateX(0)",
            transition:
              "width 0.3s cubic-bezier(0.4, 0, 0.2, 1), transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            overflow: "hidden",
          }}
        >
          {/* サイドバー上部：開閉アイコンとロゴエリア */}
          <div
            style={{
              padding: sidebarCollapsed && !isMobile ? "20px 0" : "30px 20px",
              display: "flex",
              flexDirection: "column",
              alignItems:
                sidebarCollapsed && !isMobile ? "center" : "flex-start",
              borderBottom: "1px solid rgba(255,255,255,0.1)",
              minHeight: "120px",
              boxSizing: "border-box",
            }}
          >
            {/* 開閉トグルアイコン (PCのみ表示) */}
            {!isMobile && (
              <div
                onClick={function () {
                  setSidebarCollapsed(!sidebarCollapsed);
                }}
                style={{
                  cursor: "pointer",
                  color: "#fff",
                  marginBottom: sidebarCollapsed ? "0" : "20px",
                }}
              >
                <Menu size={24} />
              </div>
            )}

            {/* モバイル用閉じボタン */}
            {isMobile && (
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <X
                  size={24}
                  onClick={function () {
                    setSidebarOpen(false);
                  }}
                  style={{ cursor: "pointer", color: "#fff" }}
                />
              </div>
            )}

            {/* ロゴとバージョン表示 (開いている時のみ) */}
            {(!sidebarCollapsed || isMobile) && (
              <div
                style={{
                  marginTop: "10px",
                  textAlign: "center",
                  width: "100%",
                }}
              >
                <img
                  src={logoImage}
                  alt="logo"
                  style={{ width: "140px", display: "block", margin: "0 auto" }}
                />
                <div
                  style={{
                    fontSize: "10px",
                    opacity: 0.6,
                    marginTop: "5px",
                    letterSpacing: "1px",
                  }}
                >
                  v1.0.60
                </div>
              </div>
            )}
          </div>

          <nav style={{ ...commonStyles.sidebarNav, padding: "20px 0" }}>
            {[
              {
                id: "dashboard",
                label: "ダッシュボード",
                icon: LayoutDashboard,
              },
              { id: "aspects", label: "アスペクト", icon: Zap },
              { id: "midpoint", label: "ミッドポイント", icon: Crosshair },
              {
                id: "solar_arc_midpoint",
                label: "ソーラーアーク・ディレクション",
                icon: Target,
              },
              { id: "chart_analysis", label: "チャート分析", icon: TrendingUp },
              { id: "moon", label: "月相", icon: Moon },
              {
                id: "ephemeris_chart",
                label: "エフェリメス・チャート",
                icon: CircleDot,
              },
              { id: "planet_return", label: "惑星リターン", icon: RefreshCw },
              { id: "declination", label: "赤緯", icon: MoveVertical },
              {
                id: "calendar",
                label: "天体イベントカレンダー",
                icon: Calendar,
              },
              { id: "speed", label: "惑星逆行・スピード", icon: Gauge },
              {
                id: "aspect_search",
                label: "アスペクト検索・統計分析",
                icon: Search,
              },
              {
                id: "astrology_report",
                label: "アストロロジー・レポート",
                icon: FileText,
              },
            ].map(function (item) {
              const isActive = activeSection === item.id;
              return (
                <div
                  key={item.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent:
                      sidebarCollapsed && !isMobile ? "center" : "flex-start",
                    gap: "12px",
                    padding: "8px 25px",
                    cursor: "pointer",
                    background: isActive
                      ? "rgba(255,255,255,0.05)"
                      : "transparent",
                    // borderLeft: isActive
                    //   ? "4px solid #ffffff"
                    //   : "4px solid transparent",
                    transition: "all 0.2s ease",
                    color: "#ffffff",
                    fontSize: "14px",
                  }}
                  onClick={function () {
                    setActiveSection(item.id);
                    if (isMobile) setSidebarOpen(false);
                  }}
                >
                  <item.icon
                    size={20}
                    style={{
                      flexShrink: 0,
                      // アクティブアイコン色を #93278f に変更
                      color: "#ffffff",
                      transition: "color 0.2s ease",
                    }}
                  />
                  {(!sidebarCollapsed || isMobile) && (
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {item.label}
                      {item.id === "chart_analysis" && (
                        <span
                          style={{
                            background: "#ef4444",
                            color: "#fff",
                            fontSize: "10px",
                            padding: "2px 6px",
                            borderRadius: "4px",
                            marginLeft: "8px",
                            fontWeight: "900",
                            lineHeight: "1",
                          }}
                        >
                          PRO
                        </span>
                      )}
                    </span>
                  )}
                </div>
              );
            })}
          </nav>
        </aside>

        <main
          style={{
            ...commonStyles.mainArea,
            marginLeft: isMobile ? 0 : sidebarWidth,
            width: isMobile ? "100%" : "calc(100% - " + sidebarWidth + ")",
            padding: 0,
            transition:
              "margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1), width 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            background: isDarkMode ? "#1a191e" : "#f9fafc",
          }}
        >
          {dashboardHeader}
          {mainContent}
        </main>
        {/* 修正後：右上のハンバーガーメニュー（サイドバー開閉用） */}
        {isMobile && (
          <button
            onClick={function (e) {
              e.preventDefault();
              setSidebarOpen(true);
            }}
            style={{
              position: "fixed",
              top: "12px",
              right: "12px", // 右上に配置
              zIndex: 1150,
              // 左上の設定ボタンとデザインを統一（正円化）
              width: "44px",
              height: "44px",
              borderRadius: "50%",
              background: "#ffffff",
              color: "#000000",
              border:
                "1px solid " +
                (isDarkMode ? "rgba(255,255,255,0.1)" : "#dee2e6"),
              padding: "0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              cursor: "pointer",
            }}
          >
            <Menu size={24} />
          </button>
        )}
      </div>
    );
  };

  // ============================================================
  // 3. 最終的な App コンポーネントの表示命令 (CSS定義を含む)
  // ============================================================
  // --- [NEW] アクセス制限の判定 ---
  if (!isDashboardUnlocked && !isTrialActive && !isLocal) {
    return (
      <DashboardGate
        isDarkMode={isDarkMode}
        onUnlocked={handleUnlockDashboard}
      />
    );
  }

  return (
    <div
      className="astronica-root"
      style={{
        backgroundColor: isDarkMode ? "#1a191e" : "#f9fafc",
        color: isDarkMode ? "#ffffff" : "#686868",
        minHeight: "100vh",
        transition: "background-color 0.3s ease, color 0.3s ease",
      }}
    >
      <style>{`
        @media (max-width: 1024px) {
          .astronica-root .dashboard-wrapper { display: block !important; }
          .astronica-root main { margin-left: 0 !important; 
    padding: 0 !important; 
    width: 100% !important; }
          .astronica-root select, .astronica-root input[type="date"], .astronica-root input[type="time"] { width: 100% !important; height: 44px !important; font-size: 14px !important; display: flex !important; align-items: center !important; }
         .astronica-root [style*="display: grid"]:not(.sbc-grid-preserved):not(.preserved-grid) { 
    grid-template-columns: 1fr !important; 
    display: flex !important; 
    flex-direction: column !important; 
    padding: 10px !important; 
    gap: 5px !important;
  }
          .astronica-root [style*="grid-column"] { grid-column: span 12 !important; }
        }
        .astronica-root .btn-main-gradient { width: 100% !important; padding: 12px !important; font-weight: 700 !important; color: #fff !important; background: linear-gradient(45deg, #2c2d96 0%, #9c2393 100%) !important; border: none !important; border-radius: 25px !important; cursor: pointer !important; box-shadow: 0 4px 15px rgba(44,45,150,0.3) !important; margin-top: 15px !important; display: block !important; text-align: center !important; }
        /* DatePickerのスタイル調整 */
        .react-datepicker-wrapper { width: 100%; }
        .react-datepicker__input-container { width: 100%; }
        .react-datepicker { font-family: sans-serif; border: 1px solid #e2e8f0; box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1); }
        .react-datepicker__header { background-color: #f8fafc; border-bottom: 1px solid #e2e8f0; }
        /* スピンアニメーションの定義 */
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      {/* 初期画面を廃止し、直接ダッシュボードを描画 */}
      {renderDashboard()}

      {/* ｃｓｖパース中のローディングオーバーレイ */}
      {isParsing === true && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(255, 255, 255, 0.85)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
            color: "#717171",
            backdropFilter: "blur(4px)",
          }}
        >
          <div style={{ marginBottom: "20px" }}>
            <RefreshCw
              size={48}
              style={{
                animation: "spin 1s linear infinite",
                color: "#6e6e6e",
              }}
            />
          </div>
          <div
            style={{
              fontSize: "18px",
              fontWeight: "bold",
              letterSpacing: "0.1em",
            }}
          >
            csvをパース中...
          </div>
        </div>
      )}
    </div>
  );
}